<script lang="ts">
    import { ModeWatcher } from "mode-watcher";
    import { Toaster } from "./lib/components/ui/sonner/index.js";
    import * as Tooltip from "./lib/components/ui/tooltip/index.js";
    import { locale, locales, type Locale } from "./lib/locale.svelte.js";
    import { addDebug, debugWidget } from "./lib/components/common/debug.svelte";
    import Boundary from "./lib/components/common/boundary.svelte";
    import Router from "./router.svelte";
    import { type Page } from "./lib/route.svelte.js";
    import { signOut } from "./lib/auth.svelte.js";
    import { m } from "./lib/messages.svelte.js";
    import { setContext } from "svelte";

    const { pages }: { pages: Page[] } = $props();

    // svelte-ignore state_referenced_locally
    setContext(
        "routes",
        pages.map((x) => x.route),
    );

    $effect.pre(() => {
        document.documentElement.lang = locale.current;
    });

    $effect(() => {
        document.addEventListener(
            "click",
            e => {
                if (e.defaultPrevented) return;
                const a = e.target instanceof HTMLElement ? e.target.closest("a[href]") : null;
                if (!a) return;
                if (e.button !== 0) return;
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                const target = a.getAttribute("target");
                if (target && target.toLowerCase() !== "_self") return;
                if (a.hasAttribute("download")) return;
                const href = a.getAttribute("href");
                if (!href || href.trim() === "") return;
                if (href.startsWith("#")) return;

                let url;
                try {
                    url = new URL(href, window.location.href);
                } catch {
                    return;
                }

                if (url.origin !== window.location.origin) return;
                if (url.protocol !== "http:" && url.protocol !== "https:") return;

                e.preventDefault();
                window.history.pushState(null, "", url);
            },
            { capture: true },
        );
    });

    addDebug("environment", () => import.meta.env.VITE_ENVIRONMENT);
    addDebug(m["locale"](), () => ({
        locale: debugWidget("select", {
            value: locale.current,
            options: locales.map(x => ({ value: x, label: x })),
            onChange: x => (locale.current = x as Locale),
        }),
    }));

    addDebug("buttons", () => ({
        local: debugWidget("button", {
            title: "Local",
            onClick: () => {
                const url = new URL(window.location.href);
                url.host = "local-assistant.turian.ai";
                url.port = "5174";
                window.location.replace(url);
            },
        }),
        staging: debugWidget("button", {
            title: "Staging",
            onClick: () => {
                const url = new URL(window.location.href);
                url.host = "staging-assistant.turian.ai";
                url.port = "";
                window.location.replace(url);
            },
        }),
        production: debugWidget("button", {
            title: "Production",
            onClick: () => {
                const url = new URL(window.location.href);
                url.host = "assistant.turian.ai";
                url.port = "";
                window.location.replace(url);
            },
        }),
        signOut: debugWidget("button", {
            title: "Sign out",
            onClick: signOut,
        }),
    }));
</script>

<Boundary>
    <ModeWatcher />
    <Toaster richColors />
    <Tooltip.Provider>
        <Router {pages} />
    </Tooltip.Provider>
    {#await import("./lib/components/common/debug.svelte") then { default: Debug }}
        <Debug />
    {/await}
</Boundary>
